import type { Bi, ReviewStatus } from '@/sanskrit/lib/sanskrit/types'

import { MORE_STORIES } from '@/sanskrit/lib/content/story-library'

export interface StorySentence {
    sa: string
    hi: string
    en: string
    speaker?: string
    /** word (as written) → analysis selector, for words with more than one reading */
    hints?: Record<string, string>
    note?: Bi
}

export interface StoryQuestion {
    q: Bi
    sa?: string
    options: Array<string | Bi>
    answer: number
    explain: Bi
}

export interface Story {
    id: string
    title: Bi
    sa: string
    level: number
    kind: 'story' | 'conversation' | 'classical'
    image?: string
    summary: Bi
    sentences: StorySentence[]
    questions: StoryQuestion[]
    moral?: { sa: string; hi: string; en: string }
    vocab: string[]
    focus: Bi
    original?: { sa: string; source: string }
    status: ReviewStatus
}

export const STORIES: Story[] = [
    {
        id: 'rama-reads',
        title: { hi: 'राम पढ़ता है', en: 'Rāma reads' },
        sa: 'रामः पठति',
        level: 1,
        kind: 'story',
        image: '/images/sanskrit/story-rama.jpg',
        summary: { hi: 'पाँच छोटे वाक्य: राम की सुबह।', en: 'Five short sentences: Rāma’s morning.' },
        focus: { hi: 'लट् लकार, प्रथम पुरुष एकवचन, कर्ता और कर्म', en: 'Present tense, प्रथम पुरुष singular, subject and object' },
        sentences: [
            { sa: 'रामः बालकः अस्ति।', hi: 'राम एक बालक है।', en: 'Rāma is a boy.' },
            { sa: 'सः प्रातः उत्तिष्ठति।', hi: 'वह सुबह उठता है।', en: 'He gets up in the morning.' },
            { sa: 'सः जलं पिबति।', hi: 'वह पानी पीता है।', en: 'He drinks water.', hints: { 'जलं': 'n:जल:2:eka' } },
            { sa: 'रामः पुस्तकं पठति।', hi: 'राम पुस्तक पढ़ता है।', en: 'Rāma reads a book.', hints: { 'पुस्तकं': 'n:पुस्तक:2:eka' } },
            { sa: 'सः विद्यालयं गच्छति।', hi: 'वह विद्यालय जाता है।', en: 'He goes to school.', hints: { 'विद्यालयं': 'n:विद्यालय:2:eka' } }
        ],
        questions: [
            { q: { hi: 'राम कौन है?', en: 'Who is Rāma?' }, sa: 'रामः कः अस्ति?', options: ['बालकः', 'शिक्षकः', 'राजा'], answer: 0, explain: { hi: 'पहला वाक्य: रामः बालकः अस्ति।', en: 'The first sentence says: रामः बालकः अस्ति (Rāma is a boy).' } },
            { q: { hi: 'राम क्या पीता है?', en: 'What does Rāma drink?' }, sa: 'रामः किं पिबति?', options: ['जलम्', 'दुग्धम्', 'फलम्'], answer: 0, explain: { hi: 'सः जलं पिबति: वह पानी पीता है।', en: 'सः जलं पिबति: he drinks water.' } },
            { q: { hi: 'राम कहाँ जाता है?', en: 'Where does Rāma go?' }, sa: 'रामः कुत्र गच्छति?', options: ['विद्यालयम्', 'उद्यानम्', 'ग्रामम्'], answer: 0, explain: { hi: 'सः विद्यालयं गच्छति: वह विद्यालय जाता है। जाने का स्थान द्वितीया में है।', en: 'सः विद्यालयं गच्छति: he goes to school. The destination is in द्वितीया.' } },
            { q: { hi: 'राम कब उठता है?', en: 'When does Rāma get up?' }, sa: 'रामः कदा उत्तिष्ठति?', options: ['प्रातः', 'सायम्', 'अद्य'], answer: 0, explain: { hi: 'प्रातः = सुबह।', en: 'प्रातः means in the morning.' } }
        ],
        vocab: ['ramah', 'balakah', 'pratah', 'uttisthati', 'jalam', 'pibati', 'pustakam', 'pathati', 'vidyalayah', 'gacchati'],
        status: 'verified'
    },
    {
        id: 'my-family',
        title: { hi: 'मेरा परिवार', en: 'My family' },
        sa: 'मम परिवारः',
        level: 2,
        kind: 'story',
        image: '/images/sanskrit/story-family.jpg',
        summary: { hi: 'सीता अपने परिवार के बारे में बताती है।', en: 'Sītā describes her family.' },
        focus: { hi: 'मम (मेरा), च से जुड़े दो कर्ता, द्विवचन क्रिया', en: 'मम (my), two subjects joined by च, dual verbs' },
        sentences: [
            { sa: 'अहं सीता।', hi: 'मैं सीता हूँ।', en: 'I am Sītā.' },
            { sa: 'मम परिवारः लघुः अस्ति।', hi: 'मेरा परिवार छोटा है।', en: 'My family is small.' },
            { sa: 'मम पिता शिक्षकः अस्ति।', hi: 'मेरे पिता शिक्षक हैं।', en: 'My father is a teacher.' },
            { sa: 'मम माता चिकित्सिका अस्ति।', hi: 'मेरी माँ डॉक्टर हैं।', en: 'My mother is a doctor.' },
            { sa: 'मम एकः भ्राता अस्ति।', hi: 'मेरा एक भाई है।', en: 'I have one brother.' },
            { sa: 'तस्य नाम गोपालः।', hi: 'उसका नाम गोपाल है।', en: 'His name is Gopāla.', hints: { 'तस्य': 'p:tad-m:6:eka', 'नाम': 'n:नामन्:1:eka' } },
            {
                sa: 'प्रातः माता पिता च उद्यानं गच्छतः।',
                hi: 'सुबह माता और पिता बगीचे जाते हैं।',
                en: 'In the morning, mother and father go to the garden.',
                hints: { 'उद्यानं': 'n:उद्यान:2:eka' },
                note: { hi: 'दो कर्ता (माता + पिता) च से जुड़े हैं, इसलिए क्रिया द्विवचन: गच्छतः।', en: 'Two subjects (माता + पिता) joined by च, so the verb is dual: गच्छतः.' }
            },
            {
                sa: 'अहं भ्राता च दुग्धं पिबावः।',
                hi: 'मैं और भाई दूध पीते हैं।',
                en: 'My brother and I drink milk.',
                hints: { 'दुग्धं': 'n:दुग्ध:2:eka' },
                note: { hi: 'अहम् (उत्तम) + भ्राता (प्रथम): जब उत्तम पुरुष साथ हो तो क्रिया उत्तम पुरुष में, और दो लोग हैं इसलिए द्विवचन: पिबावः।', en: 'अहम् (उत्तम) + भ्राता (प्रथम): when the speaker is included the verb is उत्तम पुरुष, and two people make it dual: पिबावः.' }
            },
            { sa: 'सायं वयं सर्वे मिलित्वा भोजनं कुर्मः।', hi: 'शाम को हम सब मिलकर भोजन करते हैं।', en: 'In the evening we all eat together.', hints: { 'सर्वे': 'p:sarva-m:1:bahu', 'भोजनं': 'n:भोजन:2:eka' } },
            { sa: 'मम परिवारः मम प्रियः अस्ति।', hi: 'मेरा परिवार मुझे प्रिय है।', en: 'My family is dear to me.' }
        ],
        questions: [
            { q: { hi: 'सीता के पिता क्या हैं?', en: 'What is Sītā’s father?' }, options: ['शिक्षकः', 'चिकित्सकः', 'राजा'], answer: 0, explain: { hi: 'मम पिता शिक्षकः अस्ति।', en: 'मम पिता शिक्षकः अस्ति: my father is a teacher.' } },
            { q: { hi: 'भाई का नाम क्या है?', en: 'What is the brother’s name?' }, options: ['गोपालः', 'रामः', 'अर्जुनः'], answer: 0, explain: { hi: 'तस्य नाम गोपालः।', en: 'तस्य नाम गोपालः: his name is Gopāla.' } },
            {
                q: { hi: '"गच्छतः" द्विवचन क्यों है?', en: 'Why is the verb गच्छतः dual?' },
                options: [
                    { hi: 'क्योंकि दो लोग (माता और पिता) जाते हैं', en: 'Because two people (mother and father) go' },
                    { hi: 'क्योंकि यह भूतकाल है', en: 'Because it is in the past tense' },
                    { hi: 'क्योंकि बगीचा बड़ा है', en: 'Because the garden is big' }
                ],
                answer: 0,
                explain: { hi: 'च से जुड़े दो एकवचन कर्ता मिलकर द्विवचन बनाते हैं।', en: 'Two singular subjects joined by च add up to a dual.' }
            },
            {
                q: { hi: '"पिबावः" उत्तम पुरुष में क्यों है?', en: 'Why is पिबावः in उत्तम पुरुष?' },
                options: [
                    { hi: 'क्योंकि कर्ताओं में "अहम्" (वक्ता) शामिल है', en: 'Because the subjects include अहम्, the speaker' },
                    { hi: 'क्योंकि दूध पिया जाता है', en: 'Because milk is drunk' },
                    { hi: 'क्योंकि भाई छोटा है', en: 'Because the brother is young' }
                ],
                answer: 0,
                explain: { hi: 'वक्ता साथ हो तो उत्तम पुरुष (अस्मद्युत्तमः 1.4.107)।', en: 'When the speaker is among the subjects, the verb is उत्तम पुरुष (अस्मद्युत्तमः 1.4.107).' }
            }
        ],
        vocab: ['sita', 'parivarah', 'laghu', 'pita', 'siksakah', 'mata', 'cikitsika', 'bhrata', 'nama', 'gopalah', 'udyanam', 'dugdham', 'bhojanam', 'priya'],
        status: 'verified'
    },
    {
        id: 'in-the-garden',
        title: { hi: 'बगीचे में', en: 'In the garden' },
        sa: 'उद्याने',
        level: 2,
        kind: 'story',
        image: '/images/sanskrit/story-garden.jpg',
        summary: { hi: 'बगीचे में पेड़, पक्षी, बच्चे और एक मोर।', en: 'Trees, birds, children and a peacock in the garden.' },
        focus: { hi: 'एकवचन, द्विवचन, बहुवचन; सप्तमी विभक्ति (में)', en: 'Singular, dual and plural; the locative (in)' },
        sentences: [
            { sa: 'एतत् उद्यानम् अस्ति।', hi: 'यह एक बगीचा है।', en: 'This is a garden.', hints: { 'एतत्': 'p:etad-n:1:eka', 'उद्यानम्': 'n:उद्यान:1:eka' } },
            { sa: 'उद्याने वृक्षाः सन्ति।', hi: 'बगीचे में पेड़ हैं।', en: 'There are trees in the garden.', hints: { 'उद्याने': 'n:उद्यान:7:eka' } },
            { sa: 'वृक्षेषु खगाः कूजन्ति।', hi: 'पेड़ों पर पक्षी चहचहाते हैं।', en: 'Birds sing in the trees.' },
            { sa: 'द्वौ बालकौ कन्दुकेन क्रीडतः।', hi: 'दो बालक गेंद से खेलते हैं।', en: 'Two boys play with a ball.' },
            { sa: 'बालिकाः पुष्पाणि पश्यन्ति।', hi: 'बालिकाएँ फूल देखती हैं।', en: 'The girls look at the flowers.', hints: { 'पुष्पाणि': 'n:पुष्प:2:bahu', 'बालिकाः': 'n:बालिका:1:bahu' } },
            { sa: 'एकः मयूरः नृत्यति।', hi: 'एक मोर नाचता है।', en: 'A peacock dances.' },
            { sa: 'सर्वे प्रसन्नाः सन्ति।', hi: 'सब प्रसन्न हैं।', en: 'Everyone is happy.', hints: { 'सर्वे': 'p:sarva-m:1:bahu' } }
        ],
        questions: [
            { q: { hi: 'कितने बालक खेलते हैं?', en: 'How many boys play?' }, options: ['द्वौ', 'त्रयः', 'एकः'], answer: 0, explain: { hi: 'द्वौ बालकौ: दो बालक (द्विवचन)।', en: 'द्वौ बालकौ: two boys (dual).' } },
            { q: { hi: 'बालिकाएँ क्या देखती हैं?', en: 'What do the girls look at?' }, options: ['पुष्पाणि', 'वृक्षान्', 'खगान्'], answer: 0, explain: { hi: 'बालिकाः पुष्पाणि पश्यन्ति।', en: 'बालिकाः पुष्पाणि पश्यन्ति: the girls look at the flowers.' } },
            { q: { hi: 'कौन नाचता है?', en: 'Who dances?' }, options: ['मयूरः', 'बालकः', 'खगः'], answer: 0, explain: { hi: 'एकः मयूरः नृत्यति।', en: 'एकः मयूरः नृत्यति: a peacock dances.' } },
            {
                q: { hi: '"वृक्षेषु" किस विभक्ति में है?', en: 'Which case is वृक्षेषु?' },
                options: [
                    { hi: 'सप्तमी (में/पर)', en: 'सप्तमी (in, on)' },
                    { hi: 'द्वितीया', en: 'द्वितीया (object)' },
                    { hi: 'प्रथमा', en: 'प्रथमा (subject)' }
                ],
                answer: 0,
                explain: { hi: 'वृक्षेषु = पेड़ों पर: सप्तमी बहुवचन।', en: 'वृक्षेषु = in/on the trees: locative plural.' }
            }
        ],
        vocab: ['udyanam', 'vrksah', 'khagah', 'kandukah', 'balika', 'puspam', 'mayurah', 'prasanna'],
        status: 'verified'
    },
    {
        id: 'thirsty-crow',
        title: { hi: 'प्यासा कौआ', en: 'The thirsty crow' },
        sa: 'तृषितः काकः',
        level: 3,
        kind: 'story',
        image: '/images/sanskrit/story-crow.jpg',
        summary: { hi: 'एक चतुर कौआ पानी कैसे पीता है।', en: 'How a clever crow gets a drink.' },
        focus: { hi: 'लङ् लकार (भूतकाल): आसीत्, अपश्यत्, अक्षिपत्', en: 'The past tense लङ्: आसीत्, अपश्यत्, अक्षिपत्' },
        sentences: [
            { sa: 'एकः काकः आसीत्।', hi: 'एक कौआ था।', en: 'There was a crow.' },
            { sa: 'सः तृषितः आसीत्।', hi: 'वह प्यासा था।', en: 'He was thirsty.' },
            { sa: 'सः जलाय इतस्ततः अभ्रमत्।', hi: 'वह पानी के लिए इधर-उधर घूमा।', en: 'He wandered here and there for water.', note: { hi: 'जलाय = पानी के लिए (चतुर्थी: प्रयोजन)।', en: 'जलाय = for water (dative of purpose).' } },
            { sa: 'सः एकं घटम् अपश्यत्।', hi: 'उसने एक घड़ा देखा।', en: 'He saw a pot.' },
            { sa: 'घटे अल्पं जलम् आसीत्।', hi: 'घड़े में थोड़ा पानी था।', en: 'There was a little water in the pot.', hints: { 'जलम्': 'n:जल:1:eka', 'अल्पं': 'a:अल्प:1:eka' } },
            { sa: 'काकः पाषाणखण्डान् घटे अक्षिपत्।', hi: 'कौए ने कंकड़ घड़े में डाले।', en: 'The crow dropped pebbles into the pot.' },
            { sa: 'जलम् उपरि आगच्छत्।', hi: 'पानी ऊपर आ गया।', en: 'The water came up.', hints: { 'जलम्': 'n:जल:1:eka' } },
            { sa: 'काकः जलम् अपिबत्, प्रसन्नः च अभवत्।', hi: 'कौए ने पानी पिया और प्रसन्न हो गया।', en: 'The crow drank the water and was happy.', hints: { 'जलम्': 'n:जल:2:eka' } }
        ],
        moral: { sa: 'उपायेन सर्वं सिध्यति।', hi: 'उपाय से सब कुछ सिद्ध होता है।', en: 'With a good plan, everything succeeds.' },
        questions: [
            {
                q: { hi: 'कौआ इधर-उधर क्यों घूमा?', en: 'Why did the crow wander about?' },
                options: [{ hi: 'वह प्यासा था', en: 'He was thirsty' }, { hi: 'वह भूखा था', en: 'He was hungry' }, { hi: 'वह खो गया था', en: 'He was lost' }],
                answer: 0,
                explain: { hi: 'सः तृषितः आसीत्, सः जलाय इतस्ततः अभ्रमत्।', en: 'सः तृषितः आसीत् (he was thirsty), so he wandered for water.' }
            },
            { q: { hi: 'कौए ने घड़े में क्या डाला?', en: 'What did the crow drop into the pot?' }, options: ['पाषाणखण्डान्', 'फलानि', 'पत्राणि'], answer: 0, explain: { hi: 'काकः पाषाणखण्डान् घटे अक्षिपत्।', en: 'काकः पाषाणखण्डान् घटे अक्षिपत्: pebbles.' } },
            {
                q: { hi: '"अपश्यत्" किस लकार में है?', en: 'Which lakāra is अपश्यत्?' },
                options: [{ hi: 'लङ् (भूतकाल)', en: 'लङ् (past)' }, { hi: 'लट् (वर्तमान)', en: 'लट् (present)' }, { hi: 'लृट् (भविष्यत्)', en: 'लृट् (future)' }],
                answer: 0,
                explain: { hi: 'आरम्भ का अ (अट्) और अन्त का त् लङ् की पहचान हैं: अ + पश्य + त्।', en: 'The augment अ at the start and the ending त् mark लङ्: अ + पश्य + त्.' }
            },
            {
                q: { hi: 'कहानी की सीख क्या है?', en: 'What is the moral?' },
                options: [{ hi: 'उपाय से सब कुछ सिद्ध होता है', en: 'With a plan, everything succeeds' }, { hi: 'कौए काले होते हैं', en: 'Crows are black' }, { hi: 'पानी गर्म होता है', en: 'Water is warm' }],
                answer: 0,
                explain: { hi: 'उपायेन सर्वं सिध्यति।', en: 'उपायेन सर्वं सिध्यति.' }
            }
        ],
        vocab: ['kakah', 'trsita', 'itastatah', 'ghatah', 'alpa', 'pasanakhandah', 'upari', 'prasanna', 'upayah'],
        status: 'verified'
    },
    {
        id: 'lion-mouse',
        title: { hi: 'शेर और चूहा', en: 'The lion and the mouse' },
        sa: 'सिंहः मूषकः च',
        level: 4,
        kind: 'story',
        image: '/images/sanskrit/story-lion.jpg',
        summary: { hi: 'छोटा मित्र भी बड़ी सहायता कर सकता है।', en: 'Even a small friend can be a great help.' },
        focus: { hi: 'लङ्, लोट् (मुञ्च), लृट् (करिष्यामि), तुमुन् (कर्तुम्)', en: 'Past लङ्, imperative मुञ्च, future करिष्यामि, infinitive कर्तुम्' },
        sentences: [
            { sa: 'एकस्मिन् वने एकः सिंहः अवसत्।', hi: 'एक वन में एक शेर रहता था।', en: 'In a forest there lived a lion.', hints: { 'वने': 'n:वन:7:eka' } },
            { sa: 'एकदा सिंहः वृक्षस्य अधः सुप्तः आसीत्।', hi: 'एक बार शेर पेड़ के नीचे सो रहा था।', en: 'Once the lion was asleep under a tree.' },
            { sa: 'एकः मूषकः तस्य शरीरे अधावत्।', hi: 'एक चूहा उसके शरीर पर दौड़ा।', en: 'A mouse ran over his body.', hints: { 'तस्य': 'p:tad-m:6:eka', 'शरीरे': 'n:शरीर:7:eka' } },
            { sa: 'सिंहः क्रुद्धः अभवत्।', hi: 'शेर क्रोधित हो गया।', en: 'The lion became angry.' },
            { sa: 'सः मूषकं पादेन अधरत्।', hi: 'उसने चूहे को पंजे से पकड़ लिया।', en: 'He held the mouse down with his paw.' },
            {
                sa: 'मूषकः अवदत्, "हे वनराज! मां मुञ्च। अहम् अपि कदाचित् तव साहाय्यं करिष्यामि।"',
                hi: 'चूहा बोला, "हे वनराज! मुझे छोड़ दो। मैं भी कभी तुम्हारी सहायता करूँगा।"',
                en: 'The mouse said, "O king of the forest! Let me go. I too will help you some day."',
                hints: { 'मां': 'p:asmad:2:eka', 'तव': 'p:yusmad:6:eka', 'साहाय्यं': 'n:साहाय्य:2:eka' },
                note: { hi: 'मुञ्च = छोड़ो (लोट्, मध्यम पुरुष एकवचन)। करिष्यामि = करूँगा (लृट्, उत्तम पुरुष एकवचन)।', en: 'मुञ्च = let go (imperative, मध्यम singular). करिष्यामि = I will do (future, उत्तम singular).' }
            },
            { sa: 'सिंहः अहसत्, मूषकं च अमुञ्चत्।', hi: 'शेर हँसा और उसने चूहे को छोड़ दिया।', en: 'The lion laughed and let the mouse go.' },
            { sa: 'एकस्मिन् दिने सिंहः व्याधस्य जाले अपतत्।', hi: 'एक दिन शेर शिकारी के जाल में फँस गया।', en: 'One day the lion fell into a hunter’s net.', hints: { 'दिने': 'n:दिन:7:eka', 'जाले': 'n:जाल:7:eka' } },
            { sa: 'मूषकः तत्र आगच्छत्, दन्तैः जालं च अकृन्तत्।', hi: 'चूहा वहाँ आया और उसने दाँतों से जाल काट दिया।', en: 'The mouse came there and cut the net with its teeth.', hints: { 'जालं': 'n:जाल:2:eka' } },
            { sa: 'सिंहः मुक्तः अभवत्।', hi: 'शेर मुक्त हो गया।', en: 'The lion was free.' }
        ],
        moral: { sa: 'लघुः अपि जनः उपकारं कर्तुं शक्नोति।', hi: 'छोटा व्यक्ति भी उपकार कर सकता है।', en: 'Even a small person can help.' },
        questions: [
            {
                q: { hi: 'शेर क्यों क्रोधित हुआ?', en: 'Why did the lion become angry?' },
                options: [{ hi: 'चूहा उसके शरीर पर दौड़ा', en: 'The mouse ran over his body' }, { hi: 'शिकारी आ गया', en: 'A hunter came' }, { hi: 'वह भूखा था', en: 'He was hungry' }],
                answer: 0,
                explain: { hi: 'एकः मूषकः तस्य शरीरे अधावत्।', en: 'एकः मूषकः तस्य शरीरे अधावत्: a mouse ran over his body.' }
            },
            { q: { hi: 'चूहे ने जाल किससे काटा?', en: 'What did the mouse cut the net with?' }, options: ['दन्तैः', 'हस्तेन', 'पादेन'], answer: 0, explain: { hi: 'दन्तैः = दाँतों से (तृतीया बहुवचन, करण)।', en: 'दन्तैः = with teeth (instrumental plural: the means).' } },
            {
                q: { hi: '"करिष्यामि" किस लकार में है?', en: 'Which lakāra is करिष्यामि?' },
                options: [{ hi: 'लृट् (भविष्यत्)', en: 'लृट् (future)' }, { hi: 'लङ् (भूत)', en: 'लङ् (past)' }, { hi: 'लोट् (आज्ञा)', en: 'लोट् (command)' }],
                answer: 0,
                explain: { hi: 'स्य (इष्य) भविष्यत् का चिह्न है: करिष्यामि = करूँगा।', en: 'The marker स्य (इष्य) shows the future: करिष्यामि = I will do.' }
            },
            {
                q: { hi: 'कहानी की सीख?', en: 'What is the moral?' },
                options: [{ hi: 'छोटा व्यक्ति भी उपकार कर सकता है', en: 'Even a small person can help' }, { hi: 'शेर सबसे बलवान् है', en: 'The lion is the strongest' }, { hi: 'जाल मज़बूत होते हैं', en: 'Nets are strong' }],
                answer: 0,
                explain: { hi: 'लघुः अपि जनः उपकारं कर्तुं शक्नोति।', en: 'लघुः अपि जनः उपकारं कर्तुं शक्नोति.' }
            }
        ],
        vocab: ['simhah', 'vanam', 'musakah', 'sariram', 'kruddha', 'padah', 'vanarajah', 'sahayyam', 'vyadhah', 'jalam-net', 'dantah', 'upakarah'],
        status: 'verified'
    },
    {
        id: 'parichaya',
        title: { hi: 'परिचय (बातचीत)', en: 'Introductions (dialogue)' },
        sa: 'परिचयः',
        level: 2,
        kind: 'conversation',
        summary: { hi: 'मीरा और अर्जुन पहली बार मिलते हैं।', en: 'Mīrā and Arjuna meet for the first time.' },
        focus: { hi: 'भवान् / भवती (आप), नाम पूछना, कुतः (कहाँ से)', en: 'Polite "you" (भवान् / भवती), asking names, कुतः (from where)' },
        sentences: [
            { speaker: 'मीरा', sa: 'नमस्ते!', hi: 'नमस्ते!', en: 'Hello!' },
            { speaker: 'अर्जुनः', sa: 'नमस्ते!', hi: 'नमस्ते!', en: 'Hello!' },
            { speaker: 'मीरा', sa: 'भवतः नाम किम्?', hi: 'आपका नाम क्या है?', en: 'What is your name?', hints: { 'भवतः': 'p:bhavat-m:6:eka', 'नाम': 'n:नामन्:1:eka', 'किम्': 'p:kim-n:1:eka' } },
            { speaker: 'अर्जुनः', sa: 'मम नाम अर्जुनः। भवत्याः नाम किम्?', hi: 'मेरा नाम अर्जुन है। आपका नाम क्या है?', en: 'My name is Arjuna. What is your name?', hints: { 'भवत्याः': 'p:bhavat-f:6:eka', 'नाम': 'n:नामन्:1:eka', 'किम्': 'p:kim-n:1:eka' } },
            { speaker: 'मीरा', sa: 'मम नाम मीरा। भवान् कुतः आगच्छति?', hi: 'मेरा नाम मीरा है। आप कहाँ से आते हैं?', en: 'My name is Mīrā. Where do you come from?', hints: { 'नाम': 'n:नामन्:1:eka' }, note: { hi: 'भवान् के साथ क्रिया प्रथम पुरुष में: आगच्छति।', en: 'With भवान् the verb is प्रथम पुरुष: आगच्छति.' } },
            { speaker: 'अर्जुनः', sa: 'अहं दिल्लीनगरात् आगच्छामि।', hi: 'मैं दिल्ली से आता हूँ।', en: 'I come from Delhi.' },
            { speaker: 'मीरा', sa: 'भवान् किं करोति?', hi: 'आप क्या करते हैं?', en: 'What do you do?', hints: { 'किं': 'p:kim-n:2:eka' } },
            { speaker: 'अर्जुनः', sa: 'अहं छात्रः अस्मि। अहं संस्कृतं पठामि।', hi: 'मैं छात्र हूँ। मैं संस्कृत पढ़ता हूँ।', en: 'I am a student. I study Sanskrit.', hints: { 'संस्कृतं': 'n:संस्कृत:2:eka' } },
            { speaker: 'मीरा', sa: 'शोभनम्! पुनः मिलामः।', hi: 'बहुत अच्छा! फिर मिलेंगे।', en: 'Excellent! See you again.' },
            { speaker: 'अर्जुनः', sa: 'अस्तु, पुनः मिलामः।', hi: 'ठीक है, फिर मिलेंगे।', en: 'Sure, see you again.' }
        ],
        questions: [
            { q: { hi: 'अर्जुन कहाँ से आता है?', en: 'Where does Arjuna come from?' }, options: ['दिल्लीनगरात्', 'ग्रामात्', 'विद्यालयात्'], answer: 0, explain: { hi: 'अहं दिल्लीनगरात् आगच्छामि: पञ्चमी = कहाँ से।', en: 'अहं दिल्लीनगरात् आगच्छामि: the ablative means "from".' } },
            { q: { hi: 'अर्जुन क्या पढ़ता है?', en: 'What does Arjuna study?' }, options: ['संस्कृतम्', 'गणितम्', 'चित्रम्'], answer: 0, explain: { hi: 'अहं संस्कृतं पठामि।', en: 'अहं संस्कृतं पठामि: I study Sanskrit.' } },
            {
                q: { hi: 'अर्जुन के लिए "भवतः" पर मीरा के लिए "भवत्याः" क्यों?', en: 'Why भवतः for Arjuna but भवत्याः for Mīrā?' },
                options: [{ hi: 'भवत् पुरुष के लिए, भवती स्त्री के लिए', en: 'भवत् is used for a man, भवती for a woman' }, { hi: 'दोनों एक ही हैं', en: 'They are the same' }, { hi: 'भवत्याः बहुवचन है', en: 'भवत्याः is plural' }],
                answer: 0,
                explain: { hi: 'आदरसूचक "आप": भवान् (पुं.), भवती (स्त्री.)। दोनों की षष्ठी: भवतः, भवत्याः।', en: 'The polite "you": भवान् (masc.), भवती (fem.). Their genitives are भवतः and भवत्याः.' }
            }
        ],
        vocab: ['namaste', 'nama', 'bhavan', 'bhavati', 'kutah', 'agacchati', 'chatrah', 'samskrtam', 'punah'],
        status: 'verified'
    },
    {
        id: 'apane',
        title: { hi: 'दुकान पर (बातचीत)', en: 'At the shop (dialogue)' },
        sa: 'आपणे',
        level: 3,
        kind: 'conversation',
        summary: { hi: 'आम खरीदना: दाम पूछना और विनम्र अनुरोध।', en: 'Buying mangoes: asking the price and making polite requests.' },
        focus: { hi: 'षष्ठी (आम्रस्य मूल्यम्), संख्याएँ, विनम्र लोट् (ददातु)', en: 'Genitive (आम्रस्य मूल्यम्), numbers, polite imperative (ददातु)' },
        sentences: [
            { speaker: 'ग्राहकः', sa: 'नमस्ते। फलानि सन्ति वा?', hi: 'नमस्ते। फल हैं क्या?', en: 'Hello. Do you have any fruit?', hints: { 'फलानि': 'n:फल:1:bahu' } },
            { speaker: 'आपणिकः', sa: 'आम्, आम्राणि कदलीफलानि च सन्ति।', hi: 'हाँ, आम और केले हैं।', en: 'Yes, there are mangoes and bananas.', hints: { 'आम्राणि': 'n:आम्र:1:bahu', 'कदलीफलानि': 'n:कदलीफल:1:bahu' } },
            { speaker: 'ग्राहकः', sa: 'एकस्य आम्रस्य मूल्यं किम्?', hi: 'एक आम का दाम क्या है?', en: 'What is the price of one mango?', hints: { 'मूल्यं': 'n:मूल्य:1:eka', 'किम्': 'p:kim-n:1:eka' } },
            { speaker: 'आपणिकः', sa: 'एकस्य आम्रस्य मूल्यं दश रूप्यकाणि।', hi: 'एक आम का दाम दस रुपये है।', en: 'One mango costs ten rupees.', hints: { 'मूल्यं': 'n:मूल्य:1:eka', 'रूप्यकाणि': 'n:रूप्यक:1:bahu' } },
            { speaker: 'ग्राहकः', sa: 'कृपया पञ्च आम्राणि ददातु।', hi: 'कृपया पाँच आम दीजिए।', en: 'Please give me five mangoes.', hints: { 'आम्राणि': 'n:आम्र:2:bahu' }, note: { hi: 'ददातु: लोट् प्रथम पुरुष, "भवान्" के साथ विनम्र अनुरोध।', en: 'ददातु: a प्रथम पुरुष imperative used with an understood भवान् for a polite request.' } },
            { speaker: 'आपणिकः', sa: 'स्वीकरोतु।', hi: 'लीजिए।', en: 'Here, please take them.' },
            { speaker: 'ग्राहकः', sa: 'धन्यवादः।', hi: 'धन्यवाद।', en: 'Thank you.' }
        ],
        questions: [
            { q: { hi: 'एक आम का दाम?', en: 'How much does one mango cost?' }, options: ['दश रूप्यकाणि', 'पञ्च रूप्यकाणि', 'एकं रूप्यकम्'], answer: 0, explain: { hi: 'एकस्य आम्रस्य मूल्यं दश रूप्यकाणि।', en: 'एकस्य आम्रस्य मूल्यं दश रूप्यकाणि: ten rupees.' } },
            { q: { hi: 'ग्राहक कितने आम माँगता है?', en: 'How many mangoes does the customer ask for?' }, options: ['पञ्च', 'दश', 'एकम्'], answer: 0, explain: { hi: 'कृपया पञ्च आम्राणि ददातु।', en: 'कृपया पञ्च आम्राणि ददातु: five.' } }
        ],
        vocab: ['grahakah', 'apanikah', 'phalam', 'amram', 'kadaliphalam', 'mulyam', 'rupyakam', 'krpaya', 'dadati', 'dhanyavadah'],
        status: 'verified'
    },
    {
        id: 'vidya-dadati',
        title: { hi: 'विद्या ददाति विनयम् (सुभाषित)', en: 'Knowledge gives humility (a classical verse)' },
        sa: 'विद्या ददाति विनयम्',
        level: 5,
        kind: 'classical',
        summary: { hi: 'हितोपदेश का प्रसिद्ध श्लोक, पदच्छेद और अन्वय सहित।', en: 'A famous verse from the Hitopadeśa, with word division and prose order.' },
        focus: { hi: 'सन्धि-विच्छेद, पञ्चमी (से), श्लोक पढ़ना', en: 'Undoing sandhi, the ablative (from), reading a verse' },
        original: { sa: 'विद्या ददाति विनयं विनयाद्याति पात्रताम्।\nपात्रत्वाद्धनमाप्नोति धनाद्धर्मं ततः सुखम्॥', source: 'हितोपदेशः (प्रस्ताविका)' },
        sentences: [
            { sa: 'विद्या ददाति विनयम्।', hi: 'विद्या विनय देती है।', en: 'Knowledge gives humility.', hints: { 'विनयम्': 'n:विनय:2:eka' } },
            { sa: 'विनयात् याति पात्रताम्।', hi: 'विनय से (मनुष्य) योग्यता पाता है।', en: 'From humility one reaches worthiness.', note: { hi: 'मूल में: विनयाद्याति (त् → द्, झलां जशोऽन्ते)।', en: 'In the verse: विनयाद्याति (त् → द् by झलां जशोऽन्ते).' } },
            { sa: 'पात्रत्वात् धनम् आप्नोति।', hi: 'योग्यता से धन प्राप्त करता है।', en: 'From worthiness one obtains wealth.', hints: { 'धनम्': 'n:धन:2:eka' }, note: { hi: 'मूल में: पात्रत्वाद्धनमाप्नोति (द् + ध = द्ध; म् + आ = मा)।', en: 'In the verse: पात्रत्वाद्धनमाप्नोति (द् + ध = द्ध; म् + आ = मा).' } },
            { sa: 'धनात् धर्मम्, ततः सुखम्।', hi: 'धन से धर्म, और उससे सुख।', en: 'From wealth comes dharma, and from that, happiness.', hints: { 'धर्मम्': 'n:धर्म:2:eka', 'सुखम्': 'n:सुख:1:eka' } }
        ],
        questions: [
            { q: { hi: 'विद्या क्या देती है?', en: 'What does knowledge give?' }, options: ['विनयम्', 'धनम्', 'सुखम्'], answer: 0, explain: { hi: 'विद्या ददाति विनयम्।', en: 'विद्या ददाति विनयम्: humility.' } },
            {
                q: { hi: '"विनयात्" में कौन सी विभक्ति है?', en: 'Which case is विनयात्?' },
                options: [{ hi: 'पञ्चमी (से)', en: 'पञ्चमी (from)' }, { hi: 'षष्ठी (का)', en: 'षष्ठी (of)' }, { hi: 'सप्तमी (में)', en: 'सप्तमी (in)' }],
                answer: 0,
                explain: { hi: '-आत् अकारान्त शब्दों की पञ्चमी एकवचन है।', en: '-आत् is the ablative singular of a-stems.' }
            },
            {
                q: { hi: '"धनमाप्नोति" का पदच्छेद?', en: 'How does धनमाप्नोति split?' },
                options: ['धनम् + आप्नोति', 'धन + माप्नोति', 'धनमा + आप्नोति'],
                answer: 0,
                explain: { hi: 'म् + आ = मा: धनम् आप्नोति।', en: 'म् + आ is written मा: धनम् आप्नोति.' }
            }
        ],
        vocab: ['vidya', 'dadati', 'vinayah', 'patrata', 'patratvam', 'dhanam', 'dharmah', 'tatah', 'sukham'],
        status: 'verified'
    },
    // The other 42 stories are in story-library.ts.
    ...MORE_STORIES
]

export const getStory = (id: string): Story | undefined => STORIES.find(s => s.id === id)

export interface DailyPhrase {
    sa: string
    hi: string
    en: string
}

export const DAILY_PHRASES: DailyPhrase[] = [
    { sa: 'सुप्रभातम्।', hi: 'सुप्रभात।', en: 'Good morning.' },
    { sa: 'शुभरात्रिः।', hi: 'शुभ रात्रि।', en: 'Good night.' },
    { sa: 'भवान् कथम् अस्ति?', hi: 'आप कैसे हैं?', en: 'How are you? (to a man)' },
    { sa: 'भवती कथम् अस्ति?', hi: 'आप कैसी हैं?', en: 'How are you? (to a woman)' },
    { sa: 'अहं कुशली अस्मि।', hi: 'मैं ठीक हूँ (पुरुष)।', en: 'I am well. (said by a man)' },
    { sa: 'अहं कुशलिनी अस्मि।', hi: 'मैं ठीक हूँ (स्त्री)।', en: 'I am well. (said by a woman)' },
    { sa: 'मम नाम सीता।', hi: 'मेरा नाम सीता है।', en: 'My name is Sītā.' },
    { sa: 'भवतः नाम किम्?', hi: 'आपका नाम क्या है?', en: 'What is your name? (to a man)' },
    { sa: 'धन्यवादः।', hi: 'धन्यवाद।', en: 'Thank you.' },
    { sa: 'स्वागतम्।', hi: 'स्वागत है।', en: 'Welcome.' },
    { sa: 'क्षम्यताम्।', hi: 'क्षमा करें।', en: 'Sorry / excuse me.' },
    { sa: 'कृपया पुनः वदतु।', hi: 'कृपया फिर से बोलिए।', en: 'Please say it again.' },
    { sa: 'शनैः शनैः वदतु।', hi: 'धीरे-धीरे बोलिए।', en: 'Please speak slowly.' },
    { sa: 'अहं न जानामि।', hi: 'मैं नहीं जानता।', en: 'I do not know.' },
    { sa: 'भवान् संस्कृतं जानाति वा?', hi: 'क्या आप संस्कृत जानते हैं?', en: 'Do you know Sanskrit?' },
    { sa: 'आम्, अहं संस्कृतं वदामि।', hi: 'हाँ, मैं संस्कृत बोलता हूँ।', en: 'Yes, I speak Sanskrit.' },
    { sa: 'कृपया जलं ददातु।', hi: 'कृपया पानी दीजिए।', en: 'Please give me water.' },
    { sa: 'पुनः मिलामः।', hi: 'फिर मिलेंगे।', en: 'See you again.' }
]
